import { List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { INVESTMENT_TWRR_REDUCER_KEY } from './investmentTWRRReducer';

export const getResourceStore = (state) => state[INVESTMENT_TWRR_REDUCER_KEY];

export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const getIsLoading = (state) => getResourceStore(state).isLoading;
export const getError = (state) => getResourceStore(state).error;
export const getResourcesById = (state) => getResourceStore(state).resourcesById;

export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;

/*
 * NOTE: For information on what is returned by QCS, please see the 'types' page for comments
 * */

const doNotIncludeStatus = {
  NOT_ENOUGH_DATAPOINTS: true,
  INVALID_DATAPOINTS: true,
};

const statusMessages = {
  NOT_ENOUGH_DATAPOINTS: 'We are unable to calculate TWR for this account',
  INVALID_DATAPOINTS: 'We are unable to calculate TWR for this account',
};

/*
 * Get the chart graph color for data
 */
const getColorForAccount = (tempIndex, colors) => {
  const currIndex = tempIndex % colors?.length;
  if (!colors?.colorList) return;
  return colors?.colorList[currIndex];
};

const fillInMissingDates = (endDate, selectedAccount) => {
  const adjustedAccountPerformances = [];

  //  This handles filling in missing dates with the values of the previous date!
  if (selectedAccount && selectedAccount.dailyPerformance) {
    const accountPerformances = selectedAccount.dailyPerformance;

    for (let i = 0; i < accountPerformances.length; i++) {
      //  If there are two dates
      if (accountPerformances[i] && accountPerformances[i + 1]) {
        let firstDate = DateTime.fromISO(accountPerformances[i].performanceOn);
        const secondDate = DateTime.fromISO(accountPerformances[i + 1].performanceOn);

        //  And there ARE missing dates in between
        if (!firstDate.plus({ days: 1 }).equals(secondDate)) {
          //  Add the first date and its balance and the missing dates with the first dates' balance
          adjustedAccountPerformances.push({
            performance: accountPerformances[i].performance,
            performanceOn: firstDate.toISODate(),
          });
          firstDate = firstDate.plus({ days: 1 });

          while (firstDate < secondDate) {
            adjustedAccountPerformances.push({
              performance: accountPerformances[i].performance,
              performanceOn: firstDate.toISODate(),
              fakeData: true,
            });
            firstDate = firstDate.plus({ days: 1 });
          }
        } else {
          //  There are no missing dates, add the first date normally
          adjustedAccountPerformances.push({
            performance: accountPerformances[i].performance,
            performanceOn: accountPerformances[i].performanceOn,
          });
        }
        //  If there is only one date but it is NOT todays date, that means dates from then to today are missing!
      } else if (
        accountPerformances[i] &&
        !accountPerformances[i + 1] &&
        !DateTime.fromISO(accountPerformances[i].performanceOn).equals(DateTime.fromISO(endDate))
      ) {
        let firstDate = DateTime.fromISO(accountPerformances[i].performanceOn);
        const secondDate = DateTime.fromISO(endDate);

        //  Add the first date and todays date, filling in the values with the balance at the first date.
        adjustedAccountPerformances.push({
          performance: accountPerformances[i].performance,
          performanceOn: firstDate.toISODate(),
        });
        firstDate = firstDate.plus({ days: 1 });

        while (firstDate <= secondDate) {
          adjustedAccountPerformances.push({
            performance: accountPerformances[i].performance,
            performanceOn: firstDate.toISODate(),
            fakeData: true,
          });
          firstDate = firstDate.plus({ days: 1 });
        }
        //  If there is only one date and it IS todays date, just add it normally!
      } else {
        adjustedAccountPerformances.push({
          performance: accountPerformances[i].performance,
          performanceOn: accountPerformances[i].performanceOn,
        });
      }
    }

    //  Push the balances to the account object!
    const newAccount = {
      accountName: selectedAccount.accountName,
      id: selectedAccount.id,
      color: selectedAccount?.color,
      accountId: selectedAccount.accountId,
      amountChange: selectedAccount.amountChange,
      startBalance: selectedAccount.startBalance,
      endBalance: selectedAccount.endBalance,
      startOn: selectedAccount.startOn,
      endOn: selectedAccount.endOn,
      todayPerformance: selectedAccount.todayPerformance,
      totalPerformance: selectedAccount.totalPerformance,
      dailyPerformance: adjustedAccountPerformances,
      status: selectedAccount.status,
    };
    return newAccount;
  }
  return selectedAccount;
};

/*
 * Fill in the missing dates with placeholder values and sort by longest held account
 * */
export const getPerformancesByAccount = createSelector(
  (state, props) => props.endDate,
  (state, props) => props.colors,
  getResourcesById,
  (endDate, colors, performances) => {
    let listOfAccountsByPerformances = [];
    performances &&
      performances.forEach((selectedAccount) => {
        const changedAccount = fillInMissingDates(endDate, selectedAccount);
        if (changedAccount !== null) listOfAccountsByPerformances.push(changedAccount);
      });
    //  const numberOfAccounts = listOfAccountsByPerformances.length;

    /*  TODO: Find the TOTAL list element based on ID and not Account Name  */
    listOfAccountsByPerformances = listOfAccountsByPerformances.filter((account) => account.accountName !== 'Total');

    let sortedList = listOfAccountsByPerformances.sort(
      (a, b) => b.dailyPerformance?.length - a.dailyPerformance?.length,
    );

    sortedList = sortedList.map((item, index) => {
      const newItem = item;
      newItem.color = getColorForAccount(index, colors);
      return newItem;
    });

    return ImmutableList(sortedList);
  },
);

/*
 * Returns the tables for either Time Weighted Return (TWR) or Internal Rate of Return (IRR)
 * */
export const formatPerformancesForTable = createSelector(
  (state, props) => props.performancesByAccount,
  (state, props) => props.startDate,
  (state, props) => props.endDate,
  (state, props) => props.isIRR,
  (state, props) => props.irrData,
  (accountPerformances, startDate, endDate, isIRR, performancesIRR) => {
    let filteredPerformances = [];

    if (isIRR) {
      performancesIRR.forEach((irrAccount) => {
        const newItem = accountPerformances
          .filter((account) => account.id === irrAccount.id)
          .toList()
          .toArray();
        filteredPerformances.push({
          id: irrAccount?.id || null,
          color: irrAccount?.color,
          accountName: irrAccount?.category,
          specialDayPercent: typeof irrAccount.totalPerformance === 'number' ? irrAccount.totalPerformance : null,
          todayBalance: typeof newItem[0]?.endBalance === 'number' ? newItem[0]?.endBalance : null,
          status: irrAccount.status,
          statusMessage: irrAccount.statusMessage,
        });
      });
      return ImmutableList(filteredPerformances);
    }
    filteredPerformances = accountPerformances;

    //  filteredPerformances = filteredPerformances.filter((account) => !doNotIncludeStatusList.includes(account.status))

    const finalTableData = filteredPerformances.map((account) => {
      /*
       * If there is no startDate to compare to the endDate and vice versa, show NULL for specialDayPercent.
       * What is being returned by QCS is the comparison from the earliest date in the daterange with the
       * latest date in the daterange, not the exact two dates.
       * */
      const startDateHasData = account?.dailyPerformance?.filter((day) => day?.performanceOn === startDate);
      const endDateHasData = account?.dailyPerformance?.filter((day) => day?.performanceOn === endDate);
      const dateRangePercent = startDateHasData?.length && endDateHasData?.length ? account?.totalPerformance : null;

      return {
        id: account?.id,
        accountName: account?.accountName,
        color: account?.color,
        oneDayPercent:
          typeof account?.todayPerformance?.performance === 'number' && !doNotIncludeStatus[account.status]
            ? account?.todayPerformance?.performance
            : null,
        specialDayPercent: !doNotIncludeStatus[account.status] ? dateRangePercent : null,
        todayBalance:
          typeof account?.endBalance === 'number' && !doNotIncludeStatus[account.status] ? account?.endBalance : null,
        status: doNotIncludeStatus[account.status] ? account.status : null,
        statusMessage: statusMessages[account.status] || null,
      };
    });
    return ImmutableList(finalTableData);
  },
);

export const formatPerformancesForChart = createSelector(
  (state, props) => props.performancesByAccount,
  (state, props) => props.chartStartDate,
  (state, props) => props.chartEndDate,
  (accountData, chartStartDate, chartEndDate) => {
    if (!accountData) {
      return [];
    }

    const tempChartData = [];
    let start = DateTime.fromISO(chartStartDate);
    const end = DateTime.fromISO(chartEndDate).plus({ days: 1 });
    const range = []; //  The range of dates
    while (start < end) {
      range.push(start.toISODate());
      start = start.plus({ days: 1 });
    }

    //  For each day, go through each account and then add the day with account data to the tempChartData array
    range.forEach((myDate) => {
      const tempData = {
        date: myDate,
        containsPerformances: false,
      };
      accountData.forEach((account) => {
        if (!doNotIncludeStatus[account.status]) {
          account?.dailyPerformance?.forEach((performanceDate) => {
            if (performanceDate.performanceOn === myDate) {
              tempData.containsPerformances = true;
              tempData[`${account.id}-performance`] = performanceDate.performance;
            }
          });
        }
      });
      tempChartData.push(tempData);
    });

    return tempChartData;
  },
);

export const getPerformanceTotals = createSelector(
  (state, props) => props.endDate,
  (state, props) => props.colors,
  getResourcesById,
  (endDate, colors, performances) => {
    let lastPerformance;

    //  If there is only one account that is NOT Total!
    if (performances.size === 1 && performances.toList().toArray()[0].accountName !== 'Total') {
      lastPerformance = performances.toList().toArray()[0];
      // If there is only one account that IS Total
    } else {
      lastPerformance = performances
        .filter((account) => account.accountName === 'Total')
        .toList()
        .toArray()[0];
    }

    const changedAccount = fillInMissingDates(endDate, lastPerformance);
    if (changedAccount) changedAccount.color = getColorForAccount(0, colors);
    return changedAccount;
  },
);
